/**
 * PROJECT MATCHING UTILITIES
 * 
 * This file provides utilities for matching projects when ProjectName is no longer unique.
 * We use composite keys: ProjectName + ClientName + AssignedGroup to uniquely identify projects.
 * 
 * UNDO INSTRUCTIONS:
 * If you need to revert this refactoring:
 * 1. Delete this file
 * 2. Revert all files that import from this file
 * 3. Restore unique constraint on ProjectName in backend
 */

/**
 * Creates a composite key from project properties.
 * Uses quotationId when present so leads with same Group/Client/ProjectName are differentiated.
 * @param {Object} project - Project object with ProjectName, ClientName, AssignedGroup, quotationId
 * @returns {string} Unique key (quotationId if present, else composite key)
 */
export const createProjectKey = (project) => {
  if (!project) return null;
  if (project.quotationId) return String(project.quotationId).trim();
  const name = project.ProjectName || project.projectName || '';
  const client = project.ClientName || project.clientName || '';
  const groupId = project.groupid?._id || project.groupid || project.groupId || '';
  const group = groupId || project.AssignedGroup || project.assignedGroup || '';
  return `${name}||${client}||${group}`.toLowerCase().trim();
};

/**
 * Creates a composite key from individual values
 * @param {string} projectName 
 * @param {string} clientName 
 * @param {string} assignedGroup 
 * @returns {string} Composite key
 */
export const createProjectKeyFromValues = (projectName, clientName, assignedGroup) => {
  const name = projectName || '';
  const client = clientName || '';
  const group = assignedGroup || '';
  return `${name}||${client}||${group}`.toLowerCase().trim();
};

/**
 * Finds a project by composite key match
 * @param {Array} projects - Array of projects to search
 * @param {Object} targetProject - Project to match against
 * @returns {Object|null} Matching project or null
 */
export const findProjectByCompositeKey = (projects, targetProject) => {
  if (!projects || !Array.isArray(projects) || !targetProject) return null;
  const targetKey = createProjectKey(targetProject);
  return projects.find(proj => createProjectKey(proj) === targetKey) || null;
};

/**
 * Finds a project by composite key values
 * @param {Array} projects - Array of projects to search
 * @param {string} projectName 
 * @param {string} clientName 
 * @param {string} assignedGroup 
 * @returns {Object|null} Matching project or null
 */
export const findProjectByValues = (projects, projectName, clientName, assignedGroup) => {
  if (!projects || !Array.isArray(projects)) return null;
  const targetKey = createProjectKeyFromValues(projectName, clientName, assignedGroup);
  return projects.find(proj => createProjectKey(proj) === targetKey) || null;
};

/**
 * Groups projects by composite key instead of just ProjectName
 * @param {Array} projects - Array of projects to group
 * @returns {Object} Object with composite keys as keys and arrays of projects as values
 */
export const groupProjectsByCompositeKey = (projects) => {
  if (!projects || !Array.isArray(projects)) return {};
  
  return projects.reduce((acc, project) => {
    const key = createProjectKey(project);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(project);
    return acc;
  }, {});
};

/**
 * Groups projects by ProjectName but keeps them separate if ClientName or Group differs
 * This maintains backward compatibility for some views that group by name only
 * @param {Array} projects - Array of projects to group
 * @returns {Object} Object with ProjectName as keys and arrays of projects as values
 */
export const groupProjectsByName = (projects) => {
  if (!projects || !Array.isArray(projects)) return {};
  
  return projects.reduce((acc, project) => {
    const name = (project.ProjectName || project.projectName || '').toLowerCase().trim();
    if (!acc[name]) {
      acc[name] = [];
    }
    acc[name].push(project);
    return acc;
  }, {});
};

